import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';

/**
 * System Admin › Tenants (RESTORE_PLAN R3.6, carrying the retired
 * SystemAdminDashboard's stat cards and Tenant Health tab): tenants, warnings,
 * errors, users and average configuration progress, and a card per tenant with
 * its subdomain, custom domain, users, progress, last activity and issues.
 * `GET /admin/tenant-health`.
 */

export interface TenantHealth {
  companyId: string;
  companyName: string;
  subdomain: string;
  customDomain?: string;
  status: 'healthy' | 'warning' | 'error';
  userCount: number;
  configurationProgress: number;
  implementedItems: number;
  inScopeItems: number;
  lastActivity?: string;
  issues: string[];
}

export interface TenantStats {
  totalTenants: number;
  healthyTenants: number;
  warningTenants: number;
  errorTenants: number;
  totalUsers: number;
  avgConfigProgress: number;
}

const EMPTY: TenantStats = { totalTenants: 0, healthyTenants: 0, warningTenants: 0, errorTenants: 0, totalUsers: 0, avgConfigProgress: 0 };

/** Errors first, then warnings, then the rest by name. */
export function sortTenants(tenants: TenantHealth[]): TenantHealth[] {
  const rank = { error: 0, warning: 1, healthy: 2 } as const;
  return [...tenants].sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3) || a.companyName.localeCompare(b.companyName));
}

const STATUS_BADGE = {
  healthy: { label: 'Healthy', icon: CheckCircle2, className: 'bg-green-100 text-green-800' },
  warning: { label: 'Warning', icon: AlertTriangle, className: 'bg-amber-100 text-amber-800' },
  error: { label: 'Error', icon: XCircle, className: 'bg-red-100 text-red-800' },
} as const;

export function SystemAdminTenants({ accessToken, onOpenCompany }: { accessToken: string; onOpenCompany?: (companyId: string) => void }) {
  const [tenants, setTenants] = useState<TenantHealth[]>([]);
  const [stats, setStats] = useState<TenantStats>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/tenant-health`, { headers: getAuthHeaders(accessToken) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not load tenant health');
      setTenants(Array.isArray(body.tenants) ? body.tenants : []);
      setStats({ ...EMPTY, ...(body.stats || {}) });
    } catch (e: any) {
      setError(e?.message || 'Could not load tenant health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const cards: [string, number | string][] = [
    ['Tenants', stats.totalTenants],
    ['Warnings', stats.warningTenants],
    ['Errors', stats.errorTenants],
    ['Total users', stats.totalUsers],
    ['Avg config %', `${Math.round(stats.avgConfigProgress)}%`],
  ];

  return (
    <div className="space-y-6" data-testid="system-admin-tenants">
      <div className="flex items-center justify-between">
        <div>
          <h2>Tenant health</h2>
          <p className="text-muted-foreground">Every client company: configuration progress, users, activity and what needs attention</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`size-4 mr-2 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map(([label, value]) => (
          <Card key={label}>
            <CardHeader className="pb-2"><CardDescription>{label}</CardDescription></CardHeader>
            <CardContent><div className="text-2xl" data-testid={`tenant-stat-${label}`}>{value}</div></CardContent>
          </Card>
        ))}
      </div>

      {error && <p className="text-red-600" role="alert">{error}</p>}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading tenants" /></div>
      ) : tenants.length === 0 ? (
        <p className="text-muted-foreground">No tenants yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortTenants(tenants).map((t) => {
            const badge = STATUS_BADGE[t.status] ?? STATUS_BADGE.warning;
            const Icon = badge.icon;
            return (
              <Card key={t.companyId} data-testid="tenant-card">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle>{onOpenCompany ? <button type="button" className="hover:underline text-left" onClick={() => onOpenCompany(t.companyId)}>{t.companyName}</button> : t.companyName}</CardTitle>
                      <CardDescription>{t.subdomain ? `${t.subdomain}.pathkit.pro` : 'No subdomain'}{t.customDomain ? ` · ${t.customDomain}` : ''}</CardDescription>
                    </div>
                    <Badge className={badge.className}><Icon className="size-3 mr-1" aria-hidden="true" />{badge.label}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-muted-foreground"><span>{t.userCount} users</span><span>{t.lastActivity ? `Active ${new Date(t.lastActivity).toLocaleDateString()}` : 'No activity yet'}</span></div>
                  <div>
                    <div className="flex justify-between"><span>Configuration</span><span>{t.implementedItems}/{t.inScopeItems} · {Math.round(t.configurationProgress)}%</span></div>
                    <Progress value={t.configurationProgress} className="h-2 mt-1" />
                  </div>
                  {t.issues.length > 0 && (
                    <ul className="list-disc pl-5 text-amber-800">
                      {t.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
